import { useState, useEffect, useContext } from 'src/ReactTS';
import * as AWS from 'aws-sdk';
import { ServiceContext } from '../provider/Service.provider';
import { IAuthInterface } from '../interface/user/login/IAuthInterface';
import { UserTypeEnum } from '../enum/UserTypeEnum';
import AuthPermissions from './AuthPermissions';
import { IUserSecurityDto } from '../interface/user/IUserSecurityDto';
import { IUserSecurityOfficerDto } from '../interface/user/officer/IUserSecurityOfficerDto';
import ConfigService from '../config/config.service';
import { AdminCreateUserRequest, AdminSetUserPasswordRequest, AdminUpdateUserAttributesRequest } from 'aws-sdk/clients/cognitoidentityserviceprovider';
import { RecaptchaToken } from 'src/util/Recaptcha';
import { SessionStorageEnum } from '../enum/SessionStorageEnum';
import { AuthError } from '../interface/Auth/AuthError';
import { Cache } from '@aws-amplify/cache';
import { signUp, confirmSignUp, signOut, signIn, confirmSignIn, resetPassword, resendSignUpCode, confirmResetPassword, getCurrentUser, signInWithRedirect, fetchUserAttributes } from '@aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { getIdTokenJwt } from '../interface/generic-form/IAuthSession';
import { ShowNotification } from 'src/components/common/ShowNotification/ShowNotification';

const AUTH_PROVIDER_STORAGE_KEY = 'authProvider';
const ENTRA_LOGIN_PENDING_KEY = 'entraLoginPending';

function hasOAuthCallbackParams(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  return params.has('code');
}

function isEntraLoginPending(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return sessionStorage.getItem(ENTRA_LOGIN_PENDING_KEY) === 'true' || hasOAuthCallbackParams();
}

export function useAuthProvider(): IAuthInterface {
  const { userAuthService } = useContext(ServiceContext);
  const defaultTemporaryPassword = "Alterar@Preca123";

  const [
    currentEmail,
    setCurrentEmail
  ] = useState<string | null | undefined>(undefined);
  const [
    currentUser,
    setCurrentUser
  ] = useState<any>(undefined);
  const [
    federatedMfaPending,
    setFederatedMfaPending
  ] = useState(false);
  const [
    federatedSignInLoading,
    setFederatedSignInLoading
  ] = useState<boolean>(isEntraLoginPending());


  const cacheJwtFromSession = async (): Promise<void> => {
    const { idToken, expiration } = await getIdTokenJwt();
    if (idToken && expiration) {
      Cache.setItem("jwt", {
        token: idToken,
        expiration: Math.floor(expiration.getTime() / 1000)
      });
    }
  };

  const setUserSession = async (mfaCode: string): Promise<void> => {
    try {
      Cache.setItem("jwt", null)

      const response = mfaCode ? (await userAuthService.signInWithMFA(await RecaptchaToken(), mfaCode)) : (await userAuthService.signIn(await RecaptchaToken()));

      if (response.user.type !== UserTypeEnum.Administrator) {
        throw new Error(`Não é permitido acessar o portal administrativo com acesso do tipo ${response.user.type}`)
      }

      if (response.user.active === false) {
        throw new Error('Usuário Inativo');
      }

      if (response.isMFAValidated === false) {
        throw new Error('VALIDATE_MFA');
      }

      setCurrentEmail(response.user.login);
      setUserPermissions(response.user);
      setCurrentUser(response.user);
      setFederatedMfaPending(false);

      await cacheJwtFromSession();

      sessionStorage.setItem(SessionStorageEnum.session, response.sessionToken)
    } catch (err) {
      throw err;
    }
  }

  const completeFederatedBackendSession = async (): Promise<void> => {
    setFederatedSignInLoading(true);
    try {
      await setUserSession("");
      sessionStorage.removeItem(ENTRA_LOGIN_PENDING_KEY);
    } catch (error) {
      if (error?.message === 'VALIDATE_MFA') {
        const attributes = await fetchUserAttributes().catch(() => null);
        const email = attributes?.email || attributes?.preferred_username || '';
        if (email) {
          setCurrentEmail(email);
        }
        setFederatedMfaPending(true);
        sessionStorage.removeItem(ENTRA_LOGIN_PENDING_KEY);
        setFederatedSignInLoading(false);
        await userAuthService.sendMFACode().catch(() => undefined);
        return;
      }

      await signOut().catch(() => undefined);
      Cache.setItem('jwt', null);
      sessionStorage.removeItem(SessionStorageEnum.session);
      sessionStorage.removeItem(AUTH_PROVIDER_STORAGE_KEY);
      sessionStorage.removeItem(ENTRA_LOGIN_PENDING_KEY);
      setMailAndUser(null);
      setFederatedMfaPending(false);

      const message = error?.message || error?.response?.data || 'Falha ao concluir login corporativo.';
      ShowNotification.Error('Falha ao entrar com Microsoft', message);
      throw error;
    } finally {
      setFederatedSignInLoading(false);
    }
  };

  const provider: IAuthInterface = {
    signUp: async (email: string, password: string) => {
      return signUp({
        username: email.toLowerCase().trim(),
        password,
        options: {
          userAttributes: {
            email: email.toLowerCase().trim(),
          },
        },
      });
    },

    confirmSignUp: async (email: string, code: string) => {
      return new Promise<any>(async (res, reject) => {
        try {
          res(await confirmSignUp({ username: email.toLowerCase().trim(), confirmationCode: code }));
        } catch (e) {
          const error = e as AuthError;
          if (error.code === 'UnexpectedLambdaException' || error.code === 'NotAuthorizedException') {
            res(true);
          } else {
            reject(error);
          }
        }
      });
    },

    signIn: async (email: string, password: string) => {
      try {
        const currentUser = await getCurrentUser().catch(() => null);
        if (currentUser) {
          await signOut();
        }

        Cache.setItem('jwt', null);
        sessionStorage.removeItem(SessionStorageEnum.session);
        sessionStorage.setItem(AUTH_PROVIDER_STORAGE_KEY, 'cognito');

        const result = await signIn({
          username: email.toLowerCase().trim(),
          password,
          options: {
            clientMetadata: {
              "grecaptcha": await RecaptchaToken()
            }
          }
        });

        if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          throw result;
        }

        await setUserSession(null)
      } catch (error) {
        throw error;
      }
    },

    signInWithMFA: async (email: string, password: string, mfaCode: string) => {
      try {
        const currentUser = await getCurrentUser().catch(() => null);
        if (currentUser) {
          await signOut();
        }

        Cache.setItem('jwt', null);
        sessionStorage.removeItem(SessionStorageEnum.session);

        const result = await signIn({
          username: email.toLowerCase().trim(),
          password,
          options: {
            clientMetadata: {
              "grecaptcha": await RecaptchaToken()
            }
          }
        });

        if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
          const recaptchaToken = await RecaptchaToken();
          const confirmed = await confirmSignIn({ challengeResponse: recaptchaToken });

          if (confirmed.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
            await confirmSignIn({ challengeResponse: mfaCode });
          }
        } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
          await confirmSignIn({ challengeResponse: mfaCode });
        }

        await setUserSession(mfaCode);
      } catch (error) {
        throw error;
      }
    },

    signInWithMicrosoft: async () => {
      if (!ConfigService.isEntraLoginAvailable()) {
        throw new Error('Login com Microsoft Entra ID não está configurado neste ambiente.');
      }

      // Amplify bloqueia signInWithRedirect se já houver sessão Cognito local
      // ("There is already a signed in user."). Limpa antes do redirect OAuth.
      const existingUser = await getCurrentUser().catch(() => null);
      if (existingUser) {
        await signOut();
      }

      const awsConfig = ConfigService.getConfig().awsConfig;
      sessionStorage.setItem(AUTH_PROVIDER_STORAGE_KEY, 'entra');
      sessionStorage.setItem(ENTRA_LOGIN_PENDING_KEY, 'true');
      setFederatedSignInLoading(true);
      Cache.setItem('jwt', null);
      sessionStorage.removeItem(SessionStorageEnum.session);
      setCurrentUser(null);
      setCurrentEmail(null);
      setFederatedMfaPending(false);

      await signInWithRedirect({
        provider: { custom: awsConfig.entraIdentityProviderName },
      });
    },

    completeFederatedMfa: async (mfaCode: string) => {
      await setUserSession(mfaCode);
    },

    signOut: async () => {
      Cache.setItem('jwt', null);

      const redirectUrl = ConfigService.getConfig().awsConfig.oauthRedirectSignOut
        ?.find(url => url.startsWith(window.location.origin))
        || `${window.location.origin}/login`;

      try {
        await userAuthService.signOut();
      } catch (error) {
        console.error(error);
      }

      sessionStorage.removeItem(SessionStorageEnum.session);
      const wasEntra = sessionStorage.getItem(AUTH_PROVIDER_STORAGE_KEY) === 'entra';
      sessionStorage.removeItem(AUTH_PROVIDER_STORAGE_KEY);
      sessionStorage.removeItem(ENTRA_LOGIN_PENDING_KEY);

      setCurrentUser(null);
      setCurrentEmail(null);
      setFederatedMfaPending(false);
      setFederatedSignInLoading(false);

      if (wasEntra && ConfigService.isEntraLoginAvailable()) {
        await signOut({
          global: false,
          oauth: { redirectUrl },
        });
      } else {
        await signOut();
      }
    },

    completeNewPassword: async (user: any, newPass: string) => {
      return confirmSignIn({ challengeResponse: newPass });
    },

    requestEmailConfirmation: async (email: string) => {
      return resendSignUpCode({ username: email.toLowerCase().trim() });
    },

    forgotPassword: async (email: string) => {
      return resetPassword({ username: email.toLowerCase().trim() });
    },

    forgotPasswordSubmit: async (email: string, code: string, newPassword: string) => {
      return confirmResetPassword({
        username: email.toLowerCase().trim(),
        confirmationCode: code,
        newPassword
      });
    },

    createUserCognito: (email: string, profile: string, setDefaultPassword: boolean) => {
      return new Promise<void>((resolve, reject) => {
        AWS.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;
        AWS.config.credentials = new AWS.CognitoIdentityCredentials({
          IdentityPoolId: ConfigService.getConfig().awsConfig.userPoolIdentityPoolId
        });

        (AWS.config.credentials as AWS.CognitoIdentityCredentials).refresh(function (error) {
          if (error) {
            console.log(error);
          } else {
            console.log('REFRESHED');
          }
        });
        const cognitoidentityserviceprovider = new AWS.CognitoIdentityServiceProvider();
        cognitoidentityserviceprovider.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;

        if (cognitoidentityserviceprovider.config.credentials) {
          cognitoidentityserviceprovider.config.credentials.accessKeyId = ConfigService.getConfig().awsConfig.userPoolWebClientId;
          cognitoidentityserviceprovider.config.credentials.secretAccessKey = '';
        }

        const params: AdminCreateUserRequest = {
          UserPoolId: ConfigService.getConfig().awsConfig.cognitoUserPoolId,
          Username: email.toLowerCase().trim(),
          UserAttributes: [
            {
              Name: 'email',
              Value: email.toLowerCase().trim()
            },
            {
              Name: 'email_verified',
              Value: 'true'
            },
            {
              Name: 'profile',
              Value: profile === 'Administrator' ? 'admin' : profile === 'Officer' ? 'officer' : profile === 'Lawyer' ? 'legal' : 'cartorio'
            }
          ]
        };

        if (setDefaultPassword) {
          params.TemporaryPassword = defaultTemporaryPassword;
        }

        cognitoidentityserviceprovider.adminCreateUser(params, function (err, data) {
          if (err) {
            console.log('ERRO createUserCognito', err, err.stack);
          } else {
            console.log('SUCCESS createUserCognito', data);
          }
        });
      });
    },

    resetPasswodCognito: (email: string, profile: string, setDefaultPassword: boolean) => {
      return new Promise<void>((resolve, reject) => {
        AWS.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;
        AWS.config.credentials = new AWS.CognitoIdentityCredentials({
          IdentityPoolId: ConfigService.getConfig().awsConfig.userPoolIdentityPoolId
        });
        const cognitoidentityserviceprovider = new AWS.CognitoIdentityServiceProvider();

        cognitoidentityserviceprovider.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;

        if (cognitoidentityserviceprovider.config.credentials) {
          cognitoidentityserviceprovider.config.credentials.accessKeyId = ConfigService.getConfig().awsConfig.userPoolWebClientId;
          cognitoidentityserviceprovider.config.credentials.secretAccessKey = '';
        }

        const paramsUpdate: AdminUpdateUserAttributesRequest = {
          UserPoolId: ConfigService.getConfig().awsConfig.cognitoUserPoolId,
          Username: email.toLowerCase().trim(),
          UserAttributes: [
            {
              Name: 'email',
              Value: email.toLowerCase().trim()
            },
            {
              Name: 'email_verified',
              Value: 'true'
            },
            {
              Name: 'profile',
              Value: profile === 'Administrator' ? 'admin' : profile === 'Officer' ? 'officer' : profile === 'Lawyer' ? 'legal' : 'cartorio'
            }
          ]
        };
        cognitoidentityserviceprovider.adminUpdateUserAttributes(paramsUpdate, function (errUpdate, dataUpdate) {
          if (errUpdate) {
            console.log("ERRO resetPasswodCognito", errUpdate, errUpdate.stack);
          } else {
            const params: AdminCreateUserRequest = {
              Username: email.toLowerCase().trim(),
              UserPoolId: ConfigService.getConfig().awsConfig.cognitoUserPoolId,
              MessageAction: "RESEND",
              DesiredDeliveryMediums: ["EMAIL"],
            };
            if (setDefaultPassword) {
              params.TemporaryPassword = defaultTemporaryPassword;
            }
            cognitoidentityserviceprovider.adminCreateUser(params, function (err, data) {
              if (err?.code === 'UnsupportedUserStateException') {
                const setUserParams: AdminSetUserPasswordRequest = {
                  Username: email.toLowerCase().trim(),
                  UserPoolId: ConfigService.getConfig().awsConfig.cognitoUserPoolId,
                  Password: defaultTemporaryPassword,
                  Permanent: false
                };
                cognitoidentityserviceprovider.adminSetUserPassword(setUserParams, function (setPassErr, setPassData) {
                  if (setPassErr) {
                    console.log("ERRO adminSetUserPassword", err, err.stack);
                  }
                  else {
                    console.log("SUCCESS adminSetUserPassword", setPassData);
                  }
                })
              } else if (err) {
                console.log("ERRO adminCreateUser at reset", err, err.stack);
              } else {
                console.log("SUCCESS adminCreateUser at reset", data);
              }
            });
          }
        });
      });
    },

    setPasswordCognito: (email: string, password: string) => {
      return new Promise<void>((resolve, reject) => {
        AWS.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;
        AWS.config.credentials = new AWS.CognitoIdentityCredentials({
          IdentityPoolId: ConfigService.getConfig().awsConfig.userPoolIdentityPoolId
        });

        (AWS.config.credentials as AWS.CognitoIdentityCredentials).refresh(function (error) {
          if (error) {
            console.log(error);
          } else {
            console.log('REFRESHED');
          }
        });
        const cognitoidentityserviceprovider = new AWS.CognitoIdentityServiceProvider();

        cognitoidentityserviceprovider.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;

        if (cognitoidentityserviceprovider.config.credentials) {
          cognitoidentityserviceprovider.config.credentials.accessKeyId = ConfigService.getConfig().awsConfig.userPoolWebClientId;
          cognitoidentityserviceprovider.config.credentials.secretAccessKey = '';
        }

        const params = {
          Username: email,
          UserPoolId: ConfigService.getConfig().awsConfig.cognitoUserPoolId,
          Password: password
        };

        cognitoidentityserviceprovider.adminSetUserPassword(params, function (err, data) {
          if (err) {
            console.log('ERRO resetPasswodCognito', err, err.stack);
          } else {
            console.log('SUCCESS resetPasswodCognito', data);
          }
        });
      });
    },

    getUserCognito: (email: string) => {
      return new Promise<any>((resolve, reject) => {
        AWS.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;
        AWS.config.credentials = new AWS.CognitoIdentityCredentials({
          IdentityPoolId: ConfigService.getConfig().awsConfig.userPoolIdentityPoolId
        });

        (AWS.config.credentials as AWS.CognitoIdentityCredentials).refresh(function (error) {
          if (error) {
            console.log(error);
          } else {
            console.log('REFRESHED');
          }
        });

        const cognitoidentityserviceprovider = new AWS.CognitoIdentityServiceProvider();

        cognitoidentityserviceprovider.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;


        if (cognitoidentityserviceprovider.config.credentials) {
          cognitoidentityserviceprovider.config.credentials.accessKeyId = ConfigService.getConfig().awsConfig.userPoolWebClientId;
          cognitoidentityserviceprovider.config.credentials.secretAccessKey = '';
        }

        const params = {
          Username: email,
          UserPoolId: ConfigService.getConfig().awsConfig.cognitoUserPoolId
        };

        cognitoidentityserviceprovider.adminGetUser(params, function (err, data) {
          if (err) {
            console.log('ERRO getUserCognito', err, err.stack);
          } else {
            console.log('SUCCESS getUserCognito', data);
          }
        });
      });
    },

    sendMailToCognito: (email: string) => {
      if (provider.getUserCognito) {
        provider.getUserCognito(email)
          .then(() => provider.forgotPassword(email))
          .catch(() => provider.signUp(email, '@alterarprimeirasenha'));
      } else {
        console.error("getUserCognito is undefined");
      }
    },


    isLoggedIn: () => !!provider.currentUser || !!sessionStorage.getItem(SessionStorageEnum.session),
    hasCurentUser: () => !!provider.currentUser,
    isEntraLoginAvailable: () => ConfigService.isEntraLoginAvailable(),
    isFederatedMfaPending: () => federatedMfaPending,
    isFederatedSignInLoading: () => federatedSignInLoading,

    currentEmail,
    currentUser
  };

  const trySetUserSession = async () => {
    try {
      await setUserSession("");
    } catch (error) {
      setMailAndUser(null);
    }
  };

  useEffect(() => {
    if (provider.isLoggedIn()) {
      trySetUserSession()
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('error')) {
      setFederatedSignInLoading(false);
      sessionStorage.removeItem(AUTH_PROVIDER_STORAGE_KEY);
      sessionStorage.removeItem(ENTRA_LOGIN_PENDING_KEY);
      const description = params.get('error_description') || params.get('error') || 'Falha no login corporativo.';
      ShowNotification.Error('Falha ao entrar com Microsoft', decodeURIComponent(description.replace(/\+/g, ' ')));
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (isEntraLoginPending()) {
      setFederatedSignInLoading(true);
    }

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          completeFederatedBackendSession().catch(() => {
            setFederatedSignInLoading(false);
            sessionStorage.removeItem(ENTRA_LOGIN_PENDING_KEY);
          });
          break;
        case 'signInWithRedirect_failure':
          setFederatedSignInLoading(false);
          sessionStorage.removeItem(AUTH_PROVIDER_STORAGE_KEY);
          sessionStorage.removeItem(ENTRA_LOGIN_PENDING_KEY);
          ShowNotification.Error(
            'Falha ao entrar com Microsoft',
            'Não foi possível concluir o login corporativo. Verifique a configuração do Entra ID / Cognito.'
          );
          break;
        default:
          break;
      }
    });

    return unsubscribe;
  }, []);

  function setMailAndUser(arg) {
    setCurrentEmail(arg == null ? arg : arg.login);
    setCurrentUser(arg);
  }

  function setUserPermissions(user: IUserSecurityDto | IUserSecurityOfficerDto) {
    user.permissions = AuthPermissions(user);
  }

  return provider;
}
